import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { ChevronRight } from "lucide-react"

import api from "@/lib/api"
import { Section } from "@/components/page"
import { HomeHero } from "@/components/academy/HomeHero"
import { CourseCard, type CourseCardCourse } from "@/components/academy/CourseCard"
import { getCourseCoverUrl } from "@/lib/course-covers"

const primaryLink =
  "motion-press motion-link-arrow inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-5 text-sm font-bold text-primary-foreground transition-[filter,transform] hover:brightness-110 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"

type LoadState = "loading" | "ready" | "error"
type Source = "seedbed" | "catalog"

interface SeedbedAccess {
  seedbeds?: { seedbedName: string; institutionName: string }[]
  courses?: CourseCardCourse[]
}

// Inicio de quien aún no tiene cursos: tiene que dar ganas de empezar. Muestra los
// cursos que ese usuario puede ver: los que su semillero le habilitó y, si no tiene,
// el catálogo público. Lidera la portada real de uno de ellos; debajo, una rejilla y
// el botón a todos sus cursos disponibles. Si nada carga, el héroe sigue funcionando
// solo con el texto y el botón.
export function EmptyDashboard({ greeting }: { greeting: string }) {
  const [courses, setCourses] = useState<CourseCardCourse[]>([])
  const [state, setState] = useState<LoadState>("loading")
  const [source, setSource] = useState<Source>("catalog")
  const [seedbedName, setSeedbedName] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const loadCatalog = () =>
      api
        .get("/academy/courses?page=1&limit=12")
        .then(({ data }) => {
          if (cancelled) return
          setCourses((data.data?.courses ?? []) as CourseCardCourse[])
          setSource("catalog")
          setState("ready")
        })
        .catch(() => {
          if (!cancelled) setState("error")
        })

    // Primero lo que el semillero le habilitó; sin eso (o si falla), el catálogo público.
    api
      .get("/academy/seedbeds/me")
      .then(({ data }) => {
        const access = data.data as SeedbedAccess
        const mine = access.courses ?? []
        if (mine.length === 0) return loadCatalog()
        if (cancelled) return
        setCourses(mine)
        setSource("seedbed")
        setSeedbedName(access.seedbeds?.[0]?.seedbedName ?? null)
        setState("ready")
      })
      .catch(() => loadCatalog())

    return () => {
      cancelled = true
    }
  }, [])

  const fromSeedbed = source === "seedbed"
  const featured = courses[0]
  // El primer curso lidera el héroe; el resto van en la rejilla.
  const rest = courses.slice(1, 12)

  return (
    <div className="space-y-12">
      <HomeHero
        greeting={greeting}
        subtitle="Elige tu primer curso y empieza hoy."
        cover={featured ? getCourseCoverUrl(featured) : undefined}
        heading="Tu primer curso te espera"
        text={
          fromSeedbed && seedbedName
            ? `Tu semillero ya te habilitó cursos (${seedbedName}). Empieza por uno y avanza hacia tu certificado.`
            : "Aprende cohetería y ciencia espacial a tu ritmo, con clases en video y un certificado al terminar."
        }
      >
        {featured && (
          <Link to={`/courses/${featured.slug}`} className={primaryLink}>
            Empezar con {featured.title}
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        )}
      </HomeHero>

      {state === "loading" && (
        <div role="status" aria-label="Cargando cursos" className="grid gap-x-5 gap-y-8 grid-cols-[repeat(auto-fit,minmax(17rem,1fr))] [&>*]:max-w-[40rem]">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-3">
              <div className="aspect-[16/10] animate-pulse rounded-2xl bg-muted" />
              <div className="h-5 w-3/4 animate-pulse rounded-md bg-muted" />
            </div>
          ))}
        </div>
      )}

      {state === "ready" && rest.length > 0 && (
        <Section titleId="para-empezar" title={fromSeedbed ? "Cursos disponibles para ti" : "Para empezar"}>
          <div className="grid gap-x-5 gap-y-8 grid-cols-[repeat(auto-fit,minmax(17rem,1fr))] [&>*]:max-w-[40rem]">
            {rest.map((course, i) => (
              // En móvil solo los cuatro primeros: siete portadas apiladas son un scroll eterno.
              <div key={course.id} className={i >= 4 ? "hidden sm:block" : undefined}>
                <CourseCard course={course} />
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  )
}
